import axios from "axios";
import moment from "moment";
import i18n from "../../../../i18n";
import { DateFormat, HttpCodes } from "../../../../modules/constants";
import { getClaims } from "../../../../modules/login";

import {
  DietDairyTypes,
  NUTRITION_FIELDS,
  dietDairyTypeToString,
} from "./DietDiary.constants";

const canAddNoteMealLog = (state) => getClaims(state, "mealLog.canAddNote");
const canViewDetailMealLog = (state) =>
  getClaims(state, "mealLog.detail.canView");
const canCreateFavorites = (state) => getClaims(state, "favorites.canCreate");
const canCreateRecallEntriesMealLog = (state) =>
  getClaims(state, "mealLog.entries.canCreateRecall");
const canCreateHceMceEventEntriesMealLog = (state) =>
  getClaims(state, "mealLog.entries.canCreateHceMceEvent");

//Constants
const SET_NUTRITION_VALUES = "SET_NUTRITION_VALUES";
const SET_MEAL_TOTAL_CALORIES = "SET_MEAL_TOTAL_CALORIES";
const SET_ADD_DIET_DIARY_SUCCESS = "SET_ADD_DIET_DIARY_SUCCESS";
const SET_ADD_DIET_DIARY_ERROR = "SET_ADD_DIET_DIARY_ERROR";
const SET_NUTRITION_FIELDS = "SET_NUTRITION_FIELDS";
const SET_FOOD_DRINK_ITEMS = "SET_FOOD_DRINK_ITEMS";
const SET_DIET_DIARY_MEAL_DATE_TIME = "SET_DIET_DIARY_MEAL_DATE_TIME";
const SET_DIET_DIARY_ABOUT_MEAL = "SET_DIET_DIARY_ABOUT_MEAL";
const SET_DIET_DIARY_DATE_EXISTING_MEALS = "SET_DIET_DIARY_DATE_EXISTING_MEALS";
const SET_UPDATE_REASON_FOR = "SET_UPDATE_REASON_FOR";
const SET_REASON_NON_PARTICIPANT = "SET_REASON_NON_PARTICIPANT";
const DIET_DIARY_RESET_STATES = "DIET_DIARY_RESET_STATES";
const LOGOUT = "LOGOUT";

//Acessors
const getNutritionalInfo = (state) => state?.nutritionalInfo;
const getCalories = (state) => state?.calories;
const getChartsHelper = (state) => state?.chartsHelper;
const getIsSuccess = (state) => state?.isSuccess;
const getIsError = (state) => state?.isError;
const getNutritionFields = (state) => state?.nutritionFields;
const getSelectedItems = (state) => state?.selectedItems;
const getDateAndTimeValues = (state) => state?.dateAndTimeValues;
const getAboutMeal = (state) => state?.aboutMeal;
const getExistingDayMeals = (state) => state?.existingDayMeals;
const getUpdateReasonFor = (state) => state?.updateReasonFor;
const getReason = (state) => state?.reason;

//Default state
const initialState = {
  nutritionalInfo: [],
  calories: {
    value: "0.0 kcal",
  },
  isSuccess: false,
  isError: false,
  nutritionFields: [],
  selectedItems: [],
  dateAndTimeValues: {
    isValid: false,
    dateVals: null,
  },
  aboutMeal: null,
  existingDayMeals: [],
  updateReasonFor: "",
  reason: "",
};

export const dietDiaryReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_NUTRITION_VALUES: {
      const { nutritionalInfo } = action;
      return Object.assign({}, state, {
        nutritionalInfo: nutritionalInfo,
      });
    }
    case SET_MEAL_TOTAL_CALORIES: {
      const { calories } = action;
      return Object.assign({}, state, {
        calories: calories,
      });
    }
    case SET_ADD_DIET_DIARY_SUCCESS: {
      const { isSuccess } = action;
      return Object.assign({}, state, {
        isSuccess: isSuccess,
      });
    }
    case SET_ADD_DIET_DIARY_ERROR: {
      const { isError } = action;
      return Object.assign({}, state, {
        isError: isError,
      });
    }
    case SET_NUTRITION_FIELDS: {
      const { nutritionFields } = action;
      return Object.assign({}, state, {
        nutritionFields: nutritionFields,
      });
    }
    case SET_FOOD_DRINK_ITEMS: {
      const { selectedItems } = action;
      return Object.assign({}, state, {
        selectedItems: selectedItems,
      });
    }
    case SET_DIET_DIARY_MEAL_DATE_TIME: {
      const { dateAndTimeValues } = action;
      return Object.assign({}, state, {
        dateAndTimeValues: dateAndTimeValues,
      });
    }
    case SET_DIET_DIARY_ABOUT_MEAL: {
      const { aboutMeal } = action;
      return Object.assign({}, state, {
        aboutMeal: aboutMeal,
      });
    }
    case SET_DIET_DIARY_DATE_EXISTING_MEALS: {
      const { allMeals } = action;
      return Object.assign({}, state, {
        existingDayMeals: allMeals,
      });
    }
    case SET_UPDATE_REASON_FOR: {
      const { updateReasonFor } = action;
      return Object.assign({}, state, {
        updateReasonFor: updateReasonFor,
      });
    }
    case SET_REASON_NON_PARTICIPANT: {
      const { reason } = action;
      return Object.assign({}, state, {
        reason: reason,
      });
    }
    case DIET_DIARY_RESET_STATES: {
      return Object.assign({}, initialState, {
        nutritionFields: state.nutritionFields,
      });
    }
    case LOGOUT:
      return initialState;
    default:
      return state;
  }
};

//Mutators
export const resetStates = () => (dispatch) => {
  return dispatch({
    type: DIET_DIARY_RESET_STATES,
  });
};

export const setNutritionalInfo = (newValues) => (dispatch) => {
  return dispatch({
    type: SET_NUTRITION_VALUES,
    nutritionalInfo: newValues,
  });
};

export const setSelectedItems = (items) => (dispatch) => {
  return dispatch({
    type: SET_FOOD_DRINK_ITEMS,
    selectedItems: items,
  });
};

export const setCalories = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_MEAL_TOTAL_CALORIES,
    calories: newValue,
  });
};

export const setIsSuccess = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_ADD_DIET_DIARY_SUCCESS,
    isSuccess: newValue,
  });
};

export const setIsError = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_ADD_DIET_DIARY_ERROR,
    isError: newValue,
  });
};

export const setDateAndTimeValues = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_DIET_DIARY_MEAL_DATE_TIME,
    dateAndTimeValues: newValue,
  });
};

export const setAboutMeal = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_DIET_DIARY_ABOUT_MEAL,
    aboutMeal: newValue,
  });
};

export const setUpdateReasonFor = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_UPDATE_REASON_FOR,
    updateReasonFor: newValue,
  });
};

export const setReason = (newValue) => (dispatch) => {
  return dispatch({
    type: SET_REASON_NON_PARTICIPANT,
    reason: newValue,
  });
};

const fetchNutritionFields = () => (dispatch) => {
  dispatch({ type: SET_NUTRITION_FIELDS, nutritionFields: NUTRITION_FIELDS });
};

const saveDietDiaryMeal =
  (formData, editing, type, t) => async (dispatch, getState) => {
    if (editing) {
      return axios
        .put(`/api/diet/meal`, formData)
        .then(() => {
          return Promise.resolve(
            `${
              type === DietDairyTypes.FOOD_DRINK
                ? t("appText.dietDiary.meal")
                : dietDairyTypeToString(type)
            } ${t("appText.update.success")}!`
          );
        })
        .catch((e) => {
          if (e?.response?.status === HttpCodes.BAD_REQUEST) {
            return Promise.reject(
              i18n.t("errorText.dietDiary.meal.add.400", {
                date: moment(formData.mealDate).format(DateFormat.BASE_FORMAT),
              })
            );
          }

          return Promise.reject(i18n.t("errorText.dietDiary.meal.edit"));
        });
    } else {
      return axios
        .post(`/api/diet/meal`, formData)
        .then(() => {
          return Promise.resolve(
            `${
              type === DietDairyTypes.FOOD_DRINK
                ? t("appText.dietDiary.meal")
                : dietDairyTypeToString(type)
            } ${t("appText.add.success")}!`
          );
        })
        .catch((e) => {
          if (e?.response?.status === HttpCodes.BAD_REQUEST) {
            return Promise.reject(
              i18n.t("errorText.dietDiary.meal.add.400", {
                date: moment(formData.mealDate).format(DateFormat.BASE_FORMAT),
              })
            );
          }

          return Promise.reject(i18n.t("errorText.dietDiary.meal.add"));
        });
    }
  };

export const mapStateToProps = (state) => {
  return {
    nutritionalInfo: getNutritionalInfo(state),
    calories: getCalories(state),
    chartsHelper: getChartsHelper(state),
    isSuccess: getIsSuccess(state),
    isError: getIsError(state),
    nutritionFields: getNutritionFields(state),
    selectedItems: getSelectedItems(state),
    dateAndTimeValues: getDateAndTimeValues(state),
    aboutMeal: getAboutMeal(state),
    existingDayMeals: getExistingDayMeals(state),
    updateReasonFor: getUpdateReasonFor(state),
    reason: getReason(state),
    canAddNoteMealLog: canAddNoteMealLog(state),
    canViewDetailMealLog: canViewDetailMealLog(state),
    canCreateFavorites: canCreateFavorites(state),
    canCreateRecallEntriesMealLog: canCreateRecallEntriesMealLog(state),
    canCreateHceMceEventEntriesMealLog:
      canCreateHceMceEventEntriesMealLog(state),
  };
};

export const mapDispatchToProps = {
  setNutritionalInfo,
  setSelectedItems,
  setCalories,
  setIsSuccess,
  setIsError,
  setDateAndTimeValues,
  setAboutMeal,
  setUpdateReasonFor,
  setReason,
  saveDietDiaryMeal,
  fetchNutritionFields,
  resetStates,
};
